/**
 * CHI-23: /tickets/claim#t=<token>.
 * The token comes from the URL fragment (browsers never send it to the server), the fragment is
 * cleared at once with history.replaceState, and the token goes only in JSON POST bodies to
 * /api/tickets/claim/preview and /api/tickets/claim (server/tickets/claimHttp.ts). sessionStorage (this tab only) keeps it across the sign-in round trip;
 * it is removed at a terminal state. Never logged or rendered. Referrer is disabled on this page.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginPath } from "@/const";
import { ticketClaimCopy, type TicketClaimUiState } from "@shared/ticketClaimCopy";
import { forgetClaimToken, readAndClearClaimToken } from "@shared/ticketClaimToken";

const KNOWN = new Set(["claimable", "attached", "claimed", "expired", "revoked", "inactive", "rotated", "not_found", "rate_limited"]);

/** POSTs the token in a JSON body (never a URL). Maps any response to a UI state; never logs. */
async function postClaim(url: string, token: string): Promise<{ state: TicketClaimUiState | "sign_in_required"; eventTitle?: string | null; guestName?: string | null }> {
  try {
    const res = await fetch(url, {
      method: "POST", credentials: "same-origin", referrerPolicy: "no-referrer",
      headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
    });
    const body = (await res.json().catch(() => ({}))) as { state?: string; eventTitle?: string | null; guestName?: string | null };
    if (res.status === 401) return { state: "sign_in_required" };
    const state = body.state && KNOWN.has(body.state) ? (body.state as TicketClaimUiState) : "error";
    return { ...body, state };
  } catch {
    return { state: "error" };
  }
}

export default function TicketClaim() {
  const { user } = useAuth();
  const [token] = useState(() => (typeof window === "undefined" ? null : readAndClearClaimToken(window)));
  const [preview, setPreview] = useState<{ state: TicketClaimUiState; eventTitle?: string | null; guestName?: string | null } | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState<TicketClaimUiState | null>(null);
  const started = useRef(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    postClaim("/api/tickets/claim/preview", token).then(r => setPreview({ ...r, state: r.state === "sign_in_required" ? "claimable" : r.state }));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const state: TicketClaimUiState = !token ? "not_found" : claimed ?? preview?.state ?? "loading";
  const copy = ticketClaimCopy(state, Boolean(user));

  useEffect(() => {
    if (!["loading", "claimable", "rate_limited", "error"].includes(state)) forgetClaimToken(window);
  }, [state]);

  async function onClaim() {
    if (!token) return;
    setClaiming(true);
    const r = await postClaim("/api/tickets/claim", token);
    setClaiming(false);
    setClaimed(r.state === "sign_in_required" ? "claimable" : r.state);
  }

  const btn = "inline-flex min-h-12 items-center rounded-lg bg-emerald-800 px-6 font-semibold text-white";
  return (
    <main className="mx-auto max-w-lg px-4 py-16" aria-live="polite">
      <div className="rounded-2xl border border-white/10 bg-white/5 p-8">
        <h1 className="text-2xl font-bold">{copy.title}</h1>
        {preview?.eventTitle && (state === "claimable" || state === "claimed" || state === "attached") && (
          <dl className="mt-4 space-y-1 text-sm">
            <div><dt className="inline font-semibold">Event: </dt><dd className="inline">{preview.eventTitle}</dd></div>
            {preview.guestName && <div><dt className="inline font-semibold">Guest: </dt><dd className="inline">{preview.guestName}</dd></div>}
          </dl>
        )}
        {copy.body && <p className="mt-4 text-base leading-relaxed">{copy.body}</p>}
        <div className="mt-6">
          {copy.action === "sign_in" && <Link href={getLoginPath("/tickets/claim")} className={btn}>Sign in to add it</Link>}
          {copy.action === "claim" && (
            <button type="button" onClick={onClaim} disabled={claiming} className={`${btn} disabled:opacity-60`}>
              {claiming ? "Adding…" : "Add to my account"}
            </button>
          )}
          {copy.action === "my_events" && <Link href="/account/events" className={btn}>Go to My Events</Link>}
        </div>
      </div>
    </main>
  );
}
